<template>
    <div v-if="isVisible" class="modal-overlay">
        <div class="review-modal">
            <h3 class="text-2xl font-bold mb-4 text-gray-900">
                {{ hasExistingReview ? $translate('Edit Review') : $translate('What is your opinion?') }}
            </h3>
            <p class="mb-6 text-gray-600">
                {{ hasExistingReview ? $translate('rating_existing_review') : $translate('rating_new_review') }}
            </p>

            <form @submit.prevent="submitForm">
                <!-- Star Rating for Lector Rating -->
                <div class="mb-6">
                    <label for="lector_rating" class="block text-sm font-medium text-gray-700">
                        {{ $translate('instructorRating') }}
                    </label>
                    <div class="flex space-x-1 justify-between mt-3">
                        <font-awesome-icon v-for="n in 10" :key="'lector_' + n"
                            :icon="n <= (selectedRating.lector || hoverRating.lector) ? ['fas', 'star'] : ['far', 'star']"
                            @click="!review.readOnly && setRating('lector', n)"
                            @mouseover="!review.readOnly && previewRating('lector', n)"
                            @mouseleave="resetHoverRating('lector')"
                            class="cursor-pointer text-3xl transition-transform duration-200 ease-in-out"
                            :class="{ 'text-yellow-500': n <= (selectedRating.lector || hoverRating.lector), 'text-gray-300': n > (selectedRating.lector || hoverRating.lector) }" />
                    </div>
                    <p class="text-gray-500 text-xs mt-3" v-html="$translate('instructorRatingExplanation')"></p>
                </div>

                <!-- Other Ratings -->
                <div v-for="ratingType in ['content', 'technical']" :key="ratingType" class="mb-6">
                    <label :for="`${ratingType}_rating`" class="block text-sm font-medium text-gray-700">
                        {{ $translate(`${ratingType}Rating`) }}
                    </label>
                    <div class="flex space-x-1 justify-between mt-3">
                        <font-awesome-icon v-for="n in 10" :key="`${ratingType}_` + n"
                            :icon="n <= (selectedRating[ratingType] || hoverRating[ratingType]) ? ['fas', 'star'] : ['far', 'star']"
                            @click="!review.readOnly && setRating(ratingType, n)"
                            @mouseover="!review.readOnly && previewRating(ratingType, n)"
                            @mouseleave="resetHoverRating(ratingType)"
                            class="cursor-pointer text-3xl transition-transform duration-200 ease-in-out"
                            :class="{ 'text-yellow-500': n <= (selectedRating[ratingType] || hoverRating[ratingType]), 'text-gray-300': n > (selectedRating[ratingType] || hoverRating[ratingType]) }" />
                    </div>
                    <p class="text-gray-500 text-xs mt-3" v-html="$translate(`${ratingType}RatingExplanation`)"></p>
                </div>

                <!-- Review Textarea -->
                <div class="mb-6">
                    <label for="review" class="block text-sm font-medium text-gray-700">
                        {{ $translate('Review') }}
                    </label>
                    <textarea v-model="review.review" :disabled="review.readOnly"
                        class="block w-full border rounded-md p-3 focus:ring-2 focus:ring-blue-600 focus:outline-none"
                        rows="3"></textarea>
                    <p class="text-gray-500 text-xs mt-1">
                        {{ $translate('Please provide a detailed review of your experience.') }}</p>
                </div>

                <!-- Suggestions Textarea -->
                <div class="mb-6">
                    <label for="suggestions" class="block text-sm font-medium text-gray-700">{{
        $translate('Suggestions') }}</label>
                    <textarea v-model="review.suggestions" :disabled="review.readOnly"
                        class="block w-full border rounded-md p-3 focus:ring-2 focus:ring-blue-600 focus:outline-none"
                        rows="3"></textarea>
                    <p class="text-gray-500 text-xs mt-1">
                        {{ $translate('Any suggestions to improve the content or experience?') }}</p>
                </div>

                <!-- Checkbox and Public Name -->
                <div class="mb-6">
                    <div class="flex items-center justify-between">
                        <label for="public_visible" class="text-sm font-medium text-gray-700">
                            {{ $translate('Make review public?') }}</label>
                        <input type="checkbox" v-model="review.public_visible" :disabled="review.readOnly"
                            class="form-checkbox h-5 w-5" />
                    </div>
                    <p class="text-gray-500 text-xs">
                        {{ $translate('Enable this to make your review visible to others.') }}</p>
                    <div v-if="review.public_visible" class="mt-4">
                        <div class="flex items-center justify-between">
                            <label for="public_name" class="text-sm font-medium text-gray-700">
                                {{ $translate('Display your name publicly?') }}</label>
                            <input type="checkbox" v-model="review.public_name" :disabled="review.readOnly"
                                class="form-checkbox h-5 w-5" />
                        </div>
                        <p class="text-gray-500 text-xs">
                            {{ $translate('Enable this if you want your name to be visible with your review.') }}</p>
                    </div>
                </div>

                <!-- Buttons -->
                <div class="flex justify-end space-x-4">
                    <button @click="closeModal" type="button" class="px-4 py-2 bg-gray-300 rounded-md">{{
        $translate('Close') }}</button>
                    <button v-if="!review.readOnly" type="submit"
                        class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition">
                        {{ hasExistingReview ? $translate('Update Review') : $translate('Submit Review') }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>


<script setup>
import { ref, defineExpose } from 'vue';
import { cookie } from '@/utils/cookie';
import { notify } from 'notiwind';
import { translate } from '@/utils/translate';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faStar as fasStar } from '@fortawesome/free-solid-svg-icons';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import { sendUserAction } from '@/utils/socket'; // Import the function

// Add icons to the library
library.add(fasStar, farStar);

// Modal visibility state
const isVisible = ref(false);
const hasExistingReview = ref(false);

// Review form data
const review = ref({
    lector_rating: 0,
    content_rating: 0,
    technical_rating: 0,
    review: '',
    suggestions: '',
    public_name: true,
    public_visible: true,
});

// Selected ratings
const selectedRating = ref({
    lector: 0,
    content: 0,
    technical: 0,
});

// Temporary hover ratings
const hoverRating = ref({
    lector: 0,
    content: 0,
    technical: 0,
});

const eventId = ref(null);
const sessionId = cookie();
const brandId = window.vueAppConfig.info.id;

// Function to open the modal and fetch the review data
const openModal = (eventIdParam) => {
    sendUserAction('open', 'review');
    eventId.value = eventIdParam;
    isVisible.value = true;
    fetchReview();
};

// Function to close the modal
const closeModal = () => {
    sendUserAction('close', 'review');
    isVisible.value = false;
};

// Fetch review data for the event
const fetchReview = async () => {
    try {
        const response = await fetch(`${window.vueAppConfig.endpoint}/api/v2/events/review/fetch`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({
                event_id: eventId.value,
                session_id: sessionId,
                brand_id: brandId,
            }),
        });
        const data = await response.json();

        if (data.status) {
            hasExistingReview.value = true;
            review.value.lector_rating = data.data.lector_rating;
            review.value.content_rating = data.data.content_rating;
            review.value.technical_rating = data.data.technical_rating;
            review.value.review = data.data.review;
            review.value.suggestions = data.data.suggestions;
            selectedRating.value = {
                lector: data.data.lector_rating,
                content: data.data.content_rating,
                technical: data.data.technical_rating,
            };

            // Check if the review is allowed to be updated
            if (!data.allow_review) {
                notify({
                    title: translate('Review Approved'),
                    text: translate('Your review has already been approved and cannot be updated.'),
                    type: "warning",
                    group: "info"

                });

                // Disable the form fields to prevent updates
                review.value.readOnly = true;
            } else {
                sendUserAction('update', 'review');
                review.value.readOnly = false;
            }
        } else {
            sendUserAction('create', 'review');
            hasExistingReview.value = false;
        }
    } catch (error) {
        notify({
            title: 'Error',
            text: 'Error fetching review data.',
            type: "error",
            group: "error"
        });
    }
};

// Function to handle form submission and submit review data
const submitForm = async () => {
    try {
        const response = await fetch(`${window.vueAppConfig.endpoint}/api/v2/events/review/submit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({
                event_id: eventId.value,
                session_id: sessionId,
                brand_id: brandId,
                lector_rating: selectedRating.value.lector,
                content_rating: selectedRating.value.content,
                technical_rating: selectedRating.value.technical,
                review: review.value.review,
                suggestions: review.value.suggestions,
                public_name: review.value.public_name,
                public_visible: review.value.public_visible,
            }),
        });

        const data = await response.json();
        if (data.status) {
            notify({
                title: translate('Success'),
                text: data.message || translate('Review submitted successfully.'),
                type: "success",
                group: "info"
            });
            closeModal();
            sendUserAction('submit', 'review');
        } else {
            sendUserAction('submit_error', 'review');
            notify({
                title: translate('Error'),
                text: data.message || translate('Error submitting review.'),
                type: 'error',
            });
        }
    } catch (error) {
        notify({
            title: translate('Error'),
            text: translate('Error submitting review.'),
            type: 'error',
        });
    }
};

// Set rating on click (locks the rating)
const setRating = (type, value) => {
    selectedRating.value[type] = value;
    hoverRating.value[type] = 0; // Reset hover after selection
};

// Preview rating on hover
const previewRating = (type, value) => {
    if (!selectedRating.value[type]) {
        hoverRating.value[type] = value;
    }
};

// Reset hover rating after leaving
const resetHoverRating = (type) => {
    hoverRating.value[type] = 0;
};

// Expose the openModal method to the parent component
defineExpose({
    openModal,
});
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.6);
    z-index: 9999;
    overflow-y: auto;
    margin: 0 !important;
    padding: 1rem;
}

.review-modal {
    background-color: #fff;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    max-width: 600px;
    width: 100%;
    max-height: 90vh;
    overflow-x: auto;
}

@media (max-width: 640px) {
    .review-modal {
        padding: 1.5rem;
        max-width: 100%;
    }
}
</style>